import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Alert
} from '@mui/material'
import {
  Close,
  CheckCircle,
  Info,
  Warning,
  TipsAndUpdates,
  Business,
  Description,
  People,
  PlaylistAdd,
  Timeline
} from '@mui/icons-material'

const AyudaProcedimiento = ({ open, onClose }) => {
  const steps = [
    {
      label: 'Información Básica',
      icon: <Description />,
      description: 'Complete los datos fundamentales del procedimiento',
      details: [
        {
          title: 'Gestión',
          content: 'Seleccione la gestión a la que pertenecerá este procedimiento. Esto determina el código y la organización.'
        },
        {
          title: 'Código',
          content: 'Se genera automáticamente según la gestión seleccionada. Ejemplo: GDI-PR-001 (donde GDI es el código de gestión).'
        },
        {
          title: 'Título',
          content: 'Nombre descriptivo y claro del procedimiento. Ejemplo: "Proceso de Admisión de Estudiantes".'
        },
        {
          title: 'Proceso',
          content: 'Seleccione el proceso al que pertenece este procedimiento o cree uno nuevo si no existe.'
        },
        {
          title: 'Versión',
          content: 'Número de versión del documento (por defecto 1.0). Se incrementa con cada actualización importante.'
        }
      ]
    },
    {
      label: 'Objetivo y Alcance',
      icon: <Business />,
      description: 'Defina el propósito y los límites del procedimiento',
      details: [
        {
          title: 'Objetivo',
          content: 'Describa claramente QUÉ se busca lograr con este procedimiento. Sea específico y medible. Ejemplo: "Establecer los pasos para garantizar un proceso de admisión transparente y eficiente".'
        },
        {
          title: 'Alcance',
          content: 'Defina el ámbito de aplicación: DÓNDE, CUÁNDO y a QUIÉNES aplica. Ejemplo: "Aplica a todos los postulantes desde la publicación de convocatoria hasta la matrícula".'
        }
      ],
      tips: [
        'El objetivo debe responder: ¿Para qué sirve este procedimiento?',
        'El alcance debe especificar límites y excepciones',
        'Use lenguaje claro y preciso'
      ]
    },
    {
      label: 'Responsables',
      icon: <People />,
      description: 'Asigne los roles responsables del procedimiento',
      details: [
        {
          title: 'Roles de Responsabilidad',
          content: 'Seleccione todos los roles que participan en este procedimiento. Puede elegir múltiples roles.'
        },
        {
          title: 'Ejemplos de Roles',
          content: 'Director, Secretaria Académica, Docente, Coordinador, etc. Estos roles luego se asignarán a actividades específicas.'
        }
      ],
      tips: [
        'Agregue todos los roles que participarán en alguna actividad',
        'Puede crear nuevos roles desde la configuración del sistema',
        'Los roles ayudan a mantener la trazabilidad de responsabilidades'
      ]
    },
    {
      label: 'Definiciones y Normatividad',
      icon: <Info />,
      description: 'Agregue definiciones técnicas y referencias normativas',
      details: [
        {
          title: 'Definiciones',
          content: 'Seleccione términos técnicos o específicos que requieran aclaración. Ejemplo: "Postulante", "Matrícula", "Convalidación".'
        },
        {
          title: 'Normatividad',
          content: 'Agregue las normas, leyes o reglamentos que sustentan este procedimiento. Puede seleccionar documentos PDF previamente cargados o escribir referencias personalizadas.'
        }
      ],
      tips: [
        'Las definiciones evitan ambigüedades en la interpretación',
        'La normatividad da sustento legal al procedimiento',
        'Puede gestionar definiciones y normatividad desde Configuración'
      ]
    },
    {
      label: 'Actividades',
      icon: <PlaylistAdd />,
      description: 'Agregue las actividades paso a paso del procedimiento',
      details: [
        {
          title: 'Crear Actividades',
          content: 'Cada actividad representa un paso del procedimiento. Haga clic en "Agregar Actividad" para crear una nueva.'
        },
        {
          title: 'Título de Actividad',
          content: 'Nombre corto y descriptivo. Ejemplo: "Recibir solicitud de admisión".'
        },
        {
          title: 'Descripción',
          content: 'Detalle QUÉ se debe hacer en esta actividad. Sea específico y claro.'
        },
        {
          title: 'Información de Soporte',
          content: 'Documentos o datos necesarios para ejecutar la actividad. Ejemplo: "Formulario de admisión, Cédula del postulante".'
        },
        {
          title: 'Información Generada',
          content: 'Documentos o resultados que produce la actividad. Ejemplo: "Expediente de admisión registrado".'
        },
        {
          title: 'Responsables',
          content: 'Asigne el/los rol(es) responsable(s) de ejecutar esta actividad.'
        }
      ],
      tips: [
        'Ordene las actividades secuencialmente (puede arrastrar para reordenar)',
        'Use verbos de acción al inicio de cada título',
        'Sea específico: evite actividades muy generales o ambiguas',
        'Cada actividad debe tener al menos un responsable'
      ]
    },
    {
      label: 'Diagrama de Flujo (Opcional)',
      icon: <Timeline />,
      description: 'Genere o cree un diagrama visual del procedimiento',
      details: [
        {
          title: 'Generación Automática',
          content: 'El sistema puede generar automáticamente un diagrama de flujo basado en las actividades que creó.'
        },
        {
          title: 'Generación con IA',
          content: 'Utilice inteligencia artificial para crear un diagrama más elaborado describiendo el flujo en lenguaje natural.'
        },
        {
          title: 'Editor Manual',
          content: 'También puede crear o modificar el diagrama manualmente usando sintaxis Mermaid.'
        }
      ],
      tips: [
        'El diagrama es opcional pero altamente recomendado',
        'Facilita la comprensión visual del proceso',
        'Puede editarlo en cualquier momento después de crear el procedimiento'
      ]
    }
  ]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <TipsAndUpdates />
        Guía para Crear un Procedimiento
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Siga esta guía paso a paso para crear un procedimiento completo y bien documentado.
            Todos los campos marcados con asterisco (*) son obligatorios.
          </Typography>
        </Alert>

        <Stepper orientation="vertical" nonLinear>
          {steps.map((step, index) => (
            <Step key={index} active={true} completed={false}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {step.icon}
                  </Box>
                )}
              >
                <Typography variant="h6" fontWeight="bold">
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {step.description}
                </Typography>

                {step.details.map((detail, detailIndex) => (
                  <Paper
                    key={detailIndex}
                    elevation={0}
                    sx={{ p: 2, mb: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                      {detail.title}
                    </Typography>
                    <Typography variant="body2">
                      {detail.content}
                    </Typography>
                  </Paper>
                ))}

                {step.tips && (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Warning color="warning" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">
                        Consejos Importantes
                      </Typography>
                    </Box>
                    <List dense>
                      {step.tips.map((tip, tipIndex) => (
                        <ListItem key={tipIndex} sx={{ py: 0.5, pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={tip}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ my: 3 }} />

        <Paper elevation={0} sx={{ p: 3, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TipsAndUpdates color="success" />
            <Typography variant="h6" fontWeight="bold" color="success.dark">
              Consejos Finales
            </Typography>
          </Box>
          <List dense>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Guarde periódicamente su trabajo haciendo clic en 'Guardar'"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Puede editar el procedimiento en cualquier momento después de crearlo"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Los procedimientos se guardan como 'Borrador' hasta que los apruebe"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Revise la vista previa antes de aprobar el procedimiento"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          </List>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" size="large" fullWidth>
          Entendido, Comenzar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AyudaProcedimiento
